import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CheckIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useCallback } from 'react';
import { T, useT } from '../../translation/src';

/** The filters' values: the options toggled on per filter (strings, or a string enum). */
export type FilterValues = Record<string, string[] | undefined>;
type OptionOf<Values extends FilterValues> = NonNullable<Values[keyof Values]>[number];

/** One filter: its options' values are those of the field it sets. */
export interface FilterGroup<Values extends FilterValues> {
  key: keyof Values & string;
  title: string;
  options: { value: OptionOf<Values>; label: string }[];
}

const Chip: React.FC<{ title: string; label: string; onRemove: () => void }> = ({ title, label, onRemove }) => (
  <span className="flex h-8 items-center gap-1 whitespace-nowrap rounded-full border border-neon-green-500 bg-neon-green-500/10 pl-3 pr-1.5 text-sm">
    <span className="text-gray-500 dark:text-gray-300">{title}:</span>
    <span className="font-semibold">{label}</span>
    <button type="button" onClick={onRemove} className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10" aria-label={`${title}: ${label}`}>
      <XMarkIcon className="h-4 w-4" />
    </button>
  </span>
);

// What is on for a filter, as its options (one index access that TypeScript cannot narrow by itself).
const onFor = <Values extends FilterValues>(value: Values, key: keyof Values & string): OptionOf<Values>[] => (value[key] ?? []) as OptionOf<Values>[];

interface FilterChipsProps<Values extends FilterValues> {
  groups: FilterGroup<Values>[];
  value: Values;
  onChange: (value: Values) => void;
  // What "reset filters" goes back to; the link shows only away from it.
  defaults: Values;
}
// One button opening every filter, grouped; each option is a toggle, so several of one filter add up (any of them)
// while the filters narrow each other. What is on shows next to the button as chips, each removable, with a way
// back to the defaults. Used by the admin center and the partner's sales.
export const FilterChips = <Values extends FilterValues>({ groups, value, onChange, defaults }: FilterChipsProps<Values>) => {
  const filterLabel = useT('filter', { swc: true });
  const isOn = (key: keyof Values & string, option: OptionOf<Values>) => onFor(value, key).includes(option);
  const toggle = useCallback(
    (key: keyof Values & string, option: OptionOf<Values>) => {
      const current = onFor(value, key);
      const next = current.includes(option) ? current.filter((one) => one !== option) : [...current, option];
      onChange({ ...value, [key]: next.length ? next : undefined });
    },
    [onChange, value],
  );
  const active = groups.flatMap((group) => group.options.filter((option) => isOn(group.key, option.value)).map((option) => ({ group, option })));
  const sameAs = (a: string[] | undefined, b: string[] | undefined) => (a ?? []).length === (b ?? []).length && (a ?? []).every((one) => (b ?? []).includes(one));
  const isDefault = groups.every((group) => sameAs(value[group.key], defaults[group.key]));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover className="relative">
        <PopoverButton className="flex h-10 items-center gap-2 rounded-md border border-[#C2C6C8] bg-white px-3 text-sm outline-none transition hover:border-black dark:border-gray-300 dark:bg-[#313131] dark:hover:border-neon-green-300">
          <FunnelIcon className="h-4 w-4" />
          {filterLabel}
          {active.length > 0 && <span className="rounded-full bg-neon-green-500 px-1.5 text-xs font-semibold text-eerie-black">{active.length}</span>}
        </PopoverButton>
        <PopoverPanel
          transition
          anchor="bottom start"
          className="z-50 flex max-h-[70vh] w-80 flex-col gap-4 overflow-y-auto rounded-xl border border-gray-300 bg-white p-4 shadow-lg transition duration-200 ease-in-out [--anchor-gap:8px] data-[closed]:-translate-y-1 data-[closed]:opacity-0 dark:border-gray-500 dark:bg-[#313131] dark:text-white"
        >
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.title}</span>
              <div className="flex flex-wrap gap-1.5">
                {group.options.map((option) => {
                  const on = isOn(group.key, option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="switch"
                      aria-checked={on}
                      onClick={() => toggle(group.key, option.value)}
                      className={classNames(
                        'flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition',
                        on ? 'border-black bg-neon-green-300 font-semibold text-eerie-black dark:border-white' : 'border-gray-300 hover:border-black dark:border-gray-600 dark:hover:border-white',
                      )}
                    >
                      {/* A checkbox look: toggles add up, they do not replace each other. */}
                      <span className={classNames('flex h-3.5 w-3.5 items-center justify-center rounded-sm border', on ? 'border-eerie-black bg-eerie-black text-neon-green-300' : 'border-gray-400')}>
                        {on && <CheckIcon className="h-3 w-3" />}
                      </span>
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </PopoverPanel>
      </Popover>
      {active.map(({ group, option }) => (
        <Chip key={`${group.key}:${option.value}`} title={group.title} label={option.label} onRemove={() => toggle(group.key, option.value)} />
      ))}
      {!isDefault && (
        <button type="button" onClick={() => onChange(defaults)} className="px-1 text-sm text-gray-600 underline hover:text-neon-green-700 dark:text-gray-300 dark:hover:text-neon-green-300">
          <T _str="reset filters" swc />
        </button>
      )}
    </div>
  );
};
